import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, fileUrl } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import { hhmm, TAIPEI } from "../attendance/time";
import { Select } from "../../components/Select";
import { KioskCalendar } from "./KioskCalendar";

// 門口打卡機(業主 2026-09-25):
//   > 現在新增 獨立的打卡頁面 / 客戶的觸控螢幕是 9:16 會擺在門口 當作打卡設備
//   > 在打卡頁面還要增加公佈欄 …… 這個打卡頁面還要有今日詳細的 時間日期
//
// 直式觸控螢幕、全螢幕、沒有側邊欄。上:時間日期;中:公佈欄輪播;下:名單。
//
// ── 比例固定,名單不擠公佈欄(業主 2026-09-28) ─────────────────────
//   > 打卡+公佈欄 需要調整UI 現在如果多人會很可怕 公佈欄如果上圖片會很小!!!
// 原本名單一列 3 顆、沒有上限,人一多就往上擠,公佈欄被壓成一條、圖跟著縮。
// 同一天稍晚名單改成下拉選單(見下一段),打卡區只剩一列,公佈欄吃掉其餘全部高度。
// 有圖的公告圖片填滿,文字壓成底下一條說明。
//
// ── 下拉選單選名字,上班 / 下班兩顆都給(業主 2026-09-28) ─────────────
//   > 人員的部分 我覺得可以用其他方式選擇 用下拉選單 會更好
//   > 並且把上班 / 下班 兩個按鈕都做出來 而不是現在這樣
// **按什麼記什麼**,與目前狀態矛盾也照記(業主選的):忘了打下班、隔天直接按上班,
// 也記得進去。這取代了原本「只給現在該按的那一顆」的規矩。
// 門口畫面**不再顯示誰上班中**(業主選的),選單裡只有名字。
// 守門的是機器:只有老闆啟用過的那一台拿得到名單(後端 `routers/kiosk.py`)。
// 按錯是一定會發生的事,所以確認畫面上有「點錯了?撤銷」,十秒內有效。
//
// ── 沒人操作就回到待機畫面 ───────────────────────────────────────
// 這是一台公用的機器:選了名字就走開,下一個人看到的會是上一個人的名字。

type Status = "WORKING" | "DONE" | "NOT_YET";

interface Person {
  id: number;
  name: string;
  status: Status;
}

interface Post {
  id: number;
  body: string;
  image_url: string | null;
  author: string;
  created_at: string;
}

interface KioskState {
  device: { id: number; name: string };
  people: Person[];
  posts: Post[];
}

interface Punched {
  punch_id: number;
  kind: "IN" | "OUT";
  punched_at: string;
  name: string;
}

/** 選了名字之後,多久沒人動就清回空白。 */
const IDLE_MS = 15_000;
/** 撤銷窗口。與後端 `KIOSK_UNDO_SECONDS` 同一個數字。 */
const UNDO_MS = 10_000;
/** 公佈欄每則停多久。 */
const SLIDE_MS = 10_000;
/** 名單與公告多久重抓一次(別人剛貼的公告、別人剛打的卡)。
 *
 * 5 秒(業主 2026-09-28:「希望可以不用刷新網頁他就可以抓的到最新」)。
 * 原本 30 秒,加上新公告還要等輪播繞一圈,貼完可能一分多鐘才看得到。
 * 門口只有一台,5 秒一次對伺服器不算負擔;推播要為打卡機另開一條通道,不值得。 */
const REFRESH_MS = 5_000;


function taipeiParts(at: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TAIPEI,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

const WEEKDAY: Record<string, string> = {
  Mon: "一", Tue: "二", Wed: "三", Thu: "四", Fri: "五", Sat: "六", Sun: "日",
};

/** 今天的日期,台北時間:`2026 年 9 月 25 日(民國 115 年)星期五`。 */
export function kioskDate(at: Date): string {
  const p = taipeiParts(at);
  const year = Number(p.year);
  return `${year} 年 ${Number(p.month)} 月 ${Number(p.day)} 日(民國 ${year - 1911} 年)星期${WEEKDAY[p.weekday] ?? ""}`;
}

/** 現在時刻,台北時間 24 小時制:`14:07:08`。 */
export function kioskTime(at: Date): string {
  const p = taipeiParts(at);
  return `${p.hour}:${p.minute}:${p.second}`;
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <header className="flex flex-col items-center gap-1 pt-5">
      <time aria-label="現在時間" className="tabular text-6xl font-semibold tracking-wider text-[var(--text)]">
        {kioskTime(now)}
      </time>
      <p aria-label="今天日期" className="m-0 text-xl text-[var(--text-2)]">
        {kioskDate(now)}
      </p>
    </header>
  );
}

/** 公告的日期,月/日(台北)。 */
function postDate(iso: string): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString("zh-TW", { timeZone: TAIPEI, month: "2-digit", day: "2-digit" });
}

function Bulletin({ posts }: { posts: Post[] }) {
  const [at, setAt] = useState(0);
  // 每一次跳到新公告,輪播的計時就從頭算 —— 剛跳過去,不該兩秒後就被切走。
  const [round, setRound] = useState(0);
  const known = useRef<Set<number> | null>(null);

  // **有新公告就立刻切過去播它**(業主 2026-09-28)。新的排最前面,
  // 不跳的話要等目前這一輪播完才輪得到。第一次載入不算「新」。
  useEffect(() => {
    const ids = new Set(posts.map((one) => one.id));
    if (known.current !== null) {
      const fresh = posts.findIndex((one) => !known.current!.has(one.id));
      if (fresh >= 0) {
        setAt(fresh);
        setRound((n) => n + 1);
      }
    }
    known.current = ids;
  }, [posts]);

  useEffect(() => {
    if (posts.length < 2) return;
    const timer = window.setInterval(() => setAt((n) => (n + 1) % posts.length), SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [posts.length, round]);

  const index = posts.length > 0 ? at % posts.length : 0;
  const post = posts.length > 0 ? posts[index] : null;
  const meta = post ? [post.author, postDate(post.created_at)].filter(Boolean).join("・") : "";

  return (
    <section
      aria-label="公佈欄"
      className="flex min-h-0 flex-col gap-2 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <h2 className="m-0 text-lg font-semibold text-[var(--text-2)]">公佈欄</h2>
      {post === null ? (
        <p className="m-0 flex flex-1 items-center justify-center text-2xl text-[var(--text-3)]">目前沒有公告</p>
      ) : (
        <article className="flex min-h-0 flex-1 flex-col gap-3">
          {post.image_url ? (
            // 圖片**填滿**整塊(等比例、不裁切)。外層 min-h-0 flex-1 給它一個有上限的高度,
            // h-full 才有東西可以撐 —— 少了它,大圖會把整塊撐破,小圖則縮在角落。
            <div className="relative min-h-0 flex-1">
              <img src={fileUrl(post.image_url)} alt="公告圖片" className="absolute inset-0 h-full w-full object-contain" />
            </div>
          ) : (
            // 靠左(業主 2026-09-30:「公佈欄文字 統一靠左」),與有圖公告底下那一條說明對齊。
            <p className="m-0 flex min-h-0 flex-1 items-center overflow-hidden whitespace-pre-wrap text-left text-4xl leading-relaxed text-[var(--text)]">
              {post.body}
            </p>
          )}
          <div data-testid="caption" className="flex shrink-0 items-end justify-between gap-4 border-t border-[var(--border)] pt-2">
            <div className="min-w-0">
              {post.image_url && post.body ? (
                <p className="m-0 line-clamp-2 whitespace-pre-wrap text-2xl leading-snug text-[var(--text)]">{post.body}</p>
              ) : null}
              {meta ? <p className="m-0 text-base text-[var(--text-3)]">{meta}</p> : null}
            </div>
            {posts.length > 1 ? (
              <p className="m-0 shrink-0 text-base text-[var(--text-3)]">
                {index + 1} / {posts.length}
              </p>
            ) : null}
          </div>
        </article>
      )}
    </section>
  );
}

function NotAKiosk({ onActivated }: { onActivated: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activate = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post("/kiosk/devices", { name: "門口打卡機" });
      onActivated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "啟用不了");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
      <p className="m-0 text-3xl text-[var(--text)]">這台還不是打卡機</p>
      {user?.role === "OWNER" ? (
        <>
          <p className="m-0 text-lg text-[var(--text-2)]">
            按下去之後,這台機器就能讓同事點名字打卡。之後可以在「設定 → 打卡機」停用。
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void activate()}
            className="rounded-2xl bg-[var(--accent)] px-10 py-6 text-2xl font-semibold text-[var(--on-accent)]"
          >
            啟用這台為打卡機
          </button>
        </>
      ) : (
        <p className="m-0 text-lg text-[var(--text-2)]">請老闆在這台機器上登入系統後,打開這一頁啟用。</p>
      )}
      {error ? <p role="alert" className="m-0 text-lg text-[var(--danger)]">{error}</p> : null}
    </div>
  );
}

export function KioskPage() {
  const [device, setDevice] = useState<{ id: number; name: string } | null | undefined>(undefined);
  const [state, setState] = useState<KioskState | null>(null);
  /** 下拉選單選到的人的 id;``""`` = 還沒選。 */
  const [pickedId, setPickedId] = useState("");
  const [punched, setPunched] = useState<Punched | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idleTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.get<KioskState>("/kiosk/state"));
    } catch (err) {
      // 被停用了(或 cookie 不見了):回到「這台還不是打卡機」
      if (err instanceof ApiError && err.status === 401) {
        setDevice(null);
        setState(null);
      }
    }
  }, []);

  const whoami = useCallback(async () => {
    try {
      const got = await api.get<{ device: { id: number; name: string } | null }>("/kiosk/whoami");
      setDevice(got.device);
      if (got.device) void load();
    } catch {
      setDevice(null);
    }
  }, [load]);

  useEffect(() => {
    void whoami();
  }, [whoami]);

  useEffect(() => {
    if (!device) return;
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [device, load]);

  const backToIdle = useCallback(() => {
    setPickedId("");
    setPunched(null);
    setMessage(null);
  }, []);

  // 任何一個畫面停在「選了名字 / 打完卡 / 撤銷了」,時間到就回待機
  useEffect(() => {
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    if (pickedId === "" && punched === null && message === null) return;
    const wait = punched !== null ? UNDO_MS : IDLE_MS;
    idleTimer.current = window.setTimeout(backToIdle, wait);
    return () => {
      if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    };
  }, [pickedId, punched, message, backToIdle]);

  const punch = async (kind: "IN" | "OUT") => {
    if (pickedId === "") return;
    setBusy(true);
    try {
      const got = await api.post<Punched>("/kiosk/punch", { user_id: Number(pickedId), kind });
      setPickedId("");
      setPunched(got);
      void load();
    } catch (err) {
      setPickedId("");
      setMessage(err instanceof Error ? err.message : "打不了卡,請再試一次");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!punched) return;
    setBusy(true);
    try {
      await api.del(`/kiosk/punches/${punched.punch_id}`);
      setPunched(null);
      setMessage(`已撤銷 ${punched.name} 的那一筆`);
      void load();
    } catch (err) {
      setPunched(null);
      setMessage(err instanceof Error ? err.message : "撤銷不了,請到出勤紀錄處理");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid h-screen w-full grid-rows-[auto_minmax(0,1fr)_auto] gap-4 overflow-hidden bg-[var(--bg)] px-6 pb-6">
      <Clock />

      {device === null ? (
        <div className="row-span-2 flex min-h-0">
          <NotAKiosk onActivated={() => void whoami()} />
        </div>
      ) : null}

      {device && state ? (
        <>
          {/* 公佈欄與月曆各一半(業主 2026-09-30:「我希望分隔一半 給日歷」)。 */}
          <div className="grid min-h-0 grid-rows-2 gap-4">
            <Bulletin posts={state.posts} />
            <KioskCalendar />
          </div>

          <section aria-label="打卡" className="flex flex-col gap-3">
            <Select
              label={<span className="text-xl text-[var(--text-2)]">選擇你的名字</span>}
              options={[
                { value: "", label: "請選擇" },
                ...state.people.map((person) => ({ value: String(person.id), label: person.name })),
              ]}
              value={pickedId}
              onChange={(event) => {
                setMessage(null);
                setPunched(null);
                setPickedId(event.target.value);
              }}
              className="!h-20 !rounded-2xl !px-5 !text-3xl"
            />
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ["IN", "上班", "bg-[var(--accent)] text-[var(--on-accent)]"],
                  ["OUT", "下班", "border-2 border-[var(--accent)] bg-[var(--surface)] text-[var(--accent)]"],
                ] as const
              ).map(([kind, label, tone]) => (
                <button
                  key={kind}
                  type="button"
                  disabled={pickedId === "" || busy}
                  onClick={() => void punch(kind)}
                  className={`rounded-2xl py-8 text-4xl font-semibold disabled:opacity-40 ${tone}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        </>
      ) : null}

      {punched ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[rgba(28,25,23,0.42)] p-8">
          <div className="flex w-full max-w-xl flex-col items-center gap-8 rounded-3xl bg-[var(--surface)] p-10">
            <p role="status" className="m-0 text-center text-4xl font-semibold leading-snug text-[var(--text)]">
              {punched.name}
              <br />
              {punched.kind === "IN" ? "上班" : "下班"} {hhmm(punched.punched_at)}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void undo()}
              className="w-full rounded-2xl border border-[var(--danger)] py-5 text-2xl text-[var(--danger)]"
            >
              點錯了?撤銷(10 秒內)
            </button>
            <button
              type="button"
              onClick={backToIdle}
              className="w-full rounded-2xl bg-[var(--accent)] py-5 text-2xl text-[var(--on-accent)]"
            >
              完成
            </button>
          </div>
        </div>
      ) : null}

      {message ? (
        <div className="fixed inset-x-0 bottom-10 z-40 flex justify-center px-8">
          <p role="alert" className="m-0 rounded-2xl bg-[var(--surface)] px-8 py-5 text-2xl text-[var(--text)] shadow">
            {message}
          </p>
        </div>
      ) : null}
    </div>
  );
}
